import React, { useEffect, useRef, useState } from 'react';
import { Loader, X } from 'lucide-react';
import { MedicationTemplate } from 'src/services/interfaces/health';
import { FloatingInput } from '@components/ui/input-floating';
import { ControllerRenderProps } from 'react-hook-form';
import { SearchIcon } from '@components/shared/icon/SearchIcon';

interface MedicationInputProps {
  field: ControllerRenderProps<Record<string, unknown>, string>;
  inputValue: string;
  setInputValue: (value: string) => void;
  suggestions: MedicationTemplate[];
  setSuggestions: (suggestions: MedicationTemplate[]) => void;
  addSuggestion: (value: MedicationTemplate) => void;
  isLoading: boolean;
  error: string | null;
  setError?: (error: string) => void; // Optional callback to clear error
}

export const MedicationInput: React.FC<MedicationInputProps> = ({
  field,
  inputValue,
  setInputValue,
  suggestions,
  setSuggestions,
  addSuggestion,
  isLoading,
  error,
  setError,
}) => {
  const inputRef = useRef<HTMLDivElement>(null);
  const [showAbove, setShowAbove] = useState(false);

  useEffect(() => {
    if (suggestions?.length > 0 && inputRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const dropdownHeight = 160; // max-h-40 (160px)

      // Check if there's enough space below the input
      const spaceBelow = windowHeight - inputRect.bottom;
      const spaceAbove = inputRect.top;

      // If there's not enough space below but enough space above, show dropdown above
      setShowAbove(spaceBelow < dropdownHeight && spaceAbove > dropdownHeight);
    }
  }, [suggestions]);

  const clearInput = () => {
    setInputValue('');
    setSuggestions([]);
    if (setError) setError('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    if (setError && error) setError('');
  };

  return (
    <div className="relative" ref={inputRef}>
      <div className={`relative bg-white card-shadow border w-full flex items-center rounded-lg ${error ? 'border-red-500' : 'border-white'}`}>
        <FloatingInput
          id="search-input"
          type="text"
          inputClass="!font-secondary outline-none h-15 pl-4 pr-10 w-full !border-transparent"
          label="Enter a drug name"
          value={inputValue || ''}
          onChange={handleInputChange}
          aria-invalid={error ? 'true' : 'false'}
        />
        {isLoading && (
          <div className="absolute right-2.5 bg-white z-10">
            <Loader className="animate-spin w-8 h-8 text-primary" />
          </div>
        )}
        {inputValue ? (
          <X
            size={22}
            className="absolute cursor-pointer right-3 top-1/2 transform -translate-y-1/2 text-primary"
            onClick={clearInput}
          />
        ) : (
          <SearchIcon
            className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5.5 h-5.5"
          />
        )}
      </div>
      {error && <p className="text-red-500">{error}</p>}
      {suggestions?.length > 0 && (
        <ul
          className={`bg-white card-shadow border rounded-md shadow p-0 z-50 xl:absolute left-0 w-full max-h-40 overflow-auto ${
            showAbove ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={`${index}_${suggestion.rxcui}`}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
              onClick={() => {
                console.log(suggestion);
                addSuggestion(suggestion);
                clearInput();
              }}
            >
              {suggestion.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};